/**
 * StaffService — Gestione Servizio (CRM v6).
 *
 * Non è l'Agenda. È la vista operativa del maitre durante il turno:
 * mostra solo le prenotazioni ATTIVE di oggi, ordinate cronologicamente,
 * con pulsanti giganti per cambiare stato in un click.
 *
 * Design decisions:
 *  - Card grandi tap-friendly (target 48px+) per tablet.
 *  - Priorità cronologica (time asc).
 *  - Filtri veloci per fascia oraria (Ora / Prossime 60' / Tutte).
 *  - Alert visibili per allergie e occasioni speciali.
 *  - Auto-refresh 20s.
 *  - Same API della Agenda: /admin/reservations con scope=today.
 *
 * Estendibile in futuro (mappa sala, DnD tavoli, lista attesa, check-in)
 * senza modifiche strutturali: aggiungere widget al Grid principale.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, DoorOpen, Utensils, XCircle, Ghost, Phone, Copy as CopyIcon,
  AlertTriangle, PartyPopper, RefreshCw, Clock, Pencil, MapPin, Hash,
  Search as SearchIcon,
} from "lucide-react";

const STATUS_LABELS = {
  da_confermare: "Da confermare",
  confermata: "Confermata",
  arrivato: "Arrivato",
  completata: "Completata",
  annullata: "Annullata",
  no_show: "No-show",
};

const STATUS_STYLES = {
  da_confermare: "bg-amber-100 text-amber-900 border-amber-300",
  confermata:    "bg-emerald-100 text-emerald-900 border-emerald-300",
  arrivato:      "bg-sky-100 text-sky-900 border-sky-300",
  completata:    "bg-neutral-200 text-neutral-800 border-neutral-300",
  annullata:     "bg-rose-100 text-rose-900 border-rose-300",
  no_show:       "bg-orange-100 text-orange-900 border-orange-300",
};

const NEXT_ACTIONS = {
  da_confermare: [
    { to: "confermata", label: "Conferma", Icon: CheckCircle2, tone: "primary" },
    { to: "arrivato",   label: "Arrivato", Icon: DoorOpen,     tone: "primary" },
    { to: "annullata",  label: "Annulla",  Icon: XCircle,      tone: "danger"  },
  ],
  confermata: [
    { to: "arrivato",   label: "Arrivato", Icon: DoorOpen, tone: "primary" },
    { to: "no_show",    label: "No-show",  Icon: Ghost,     tone: "danger"  },
    { to: "annullata",  label: "Annulla",  Icon: XCircle,   tone: "danger"  },
  ],
  arrivato:  [
    { to: "completata", label: "Completa", Icon: Utensils, tone: "primary" },
    { to: "annullata",  label: "Annulla",  Icon: XCircle,  tone: "danger"  },
  ],
  completata: [], annullata: [], no_show: [],
};

// Attivi durante il servizio: quelli non-terminali.
const ACTIVE = new Set(["da_confermare", "confermata", "arrivato"]);

const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const toMinutes = (t) => {
  const [h, m] = (t || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

export default function StaffService() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");   // all | soon | now
  const [showTerminal, setShowTerminal] = useState(false);
  const [zones, setZones] = useState(["Sala", "Terrazza", "Spiaggia", "Privé"]);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reservations?scope=today");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  // Carica config sale una sola volta (statica per turno)
  useEffect(() => {
    api.get("/admin/reservations/config")
       .then((r) => { if (r.data?.zones?.length) setZones(r.data.zones); })
       .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  // Filter + sort ------------------------------------------------------
  const filtered = useMemo(() => {
    const nm = toMinutes(nowHHMM());
    let list = [...rows].sort((a, b) => toMinutes(a.time) - toMinutes(b.time));
    if (!showTerminal) list = list.filter((r) => ACTIVE.has(r.status));
    if (filter === "soon") {
      list = list.filter((r) => {
        const m = toMinutes(r.time);
        return m >= nm - 15 && m <= nm + 60;
      });
    } else if (filter === "now") {
      list = list.filter((r) => {
        const m = toMinutes(r.time);
        return Math.abs(m - nm) <= 20;
      });
    }
    // Search filter — case-insensitive, tolerant on phone.
    const q = (search || "").trim().toLowerCase();
    if (q) {
      const qDigits = q.replace(/[^0-9]/g, "");
      list = list.filter((r) => {
        const full = `${r.customer_name || ""} ${r.customer_surname || ""}`.toLowerCase();
        if (full.includes(q)) return true;
        if ((r.code || "").toLowerCase().includes(q)) return true;
        if ((r.table_number || "").toLowerCase().includes(q)) return true;
        // Phone: match on digit-tail after normalization.
        if (qDigits) {
          const phoneDigits = (r.customer_phone || "").replace(/[^0-9]/g, "");
          if (phoneDigits.includes(qDigits)) return true;
        }
        return false;
      });
    }
    return list;
  }, [rows, filter, showTerminal, search]);

  const kpi = useMemo(() => {
    const active = rows.filter((r) => ACTIVE.has(r.status));
    const now = toMinutes(nowHHMM());
    const soon = active.filter((r) => {
      const m = toMinutes(r.time);
      return m >= now - 15 && m <= now + 60;
    });
    const pax_pending = active.reduce((s, r) => s + Number(r.pax || 0), 0);
    return { active: active.length, soon: soon.length, pax_pending };
  }, [rows]);

  const change = async (r, to, opts = {}) => {
    try {
      await api.patch(`/admin/reservations/${r.id}/status`,
                       { status: to, force: !!opts.force });
      toast.success(`${r.customer_name} → ${STATUS_LABELS[to]}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assign = async (r, patch) => {
    try {
      // Uso `force=true` sulla query per skippare il double-booking check:
      // l'assegnazione tavolo/sala durante il servizio è un'azione operativa
      // e non deve mai essere bloccata da un conflitto teorico.
      await api.patch(`/admin/reservations/${r.id}?force=true`, patch);
      const bits = [];
      if (patch.zone !== undefined) bits.push(`Sala: ${patch.zone || "—"}`);
      if (patch.table_number !== undefined) bits.push(`Tavolo: ${patch.table_number || "—"}`);
      toast.success(`${r.customer_name} → ${bits.join(" · ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyPhone = (r) => {
    navigator.clipboard.writeText(r.customer_phone || "");
    toast.success("Telefono copiato");
  };

  return (
    <div className="space-y-6" data-testid="staff-service-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Servizio · {new Date().toLocaleDateString("it-IT",
            { weekday: "long", day: "2-digit", month: "long" })}</div>
          <h1 className="font-serif text-3xl mt-1">Gestione Servizio</h1>
          <p className="text-sm text-ink-500 mt-1">
            Le prenotazioni del turno · pulsanti giganti · 1-click, senza popup.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="service-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
        </Button>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Attive ora" value={kpi.active} testid="svc-kpi-active" />
        <Kpi label="Prossima ora" value={kpi.soon}  testid="svc-kpi-soon" />
        <Kpi label="Coperti previsti" value={kpi.pax_pending} testid="svc-kpi-pax" />
      </div>

      {/* Search bar — sempre visibile, filtro immediato */}
      <div className="relative" data-testid="svc-search-wrap">
        <SearchIcon className="absolute left-3 top-3 w-5 h-5 text-ink-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca nome, cognome, telefono, codice, tavolo…"
          data-testid="svc-search"
          className="w-full h-11 pl-11 pr-3 rounded-2xl border border-sand-300 bg-white
                      text-base font-sans focus:outline-none focus:border-bronze-500" />
        {search && (
          <button onClick={() => setSearch("")} data-testid="svc-search-clear"
                    className="absolute right-3 top-2.5 text-ink-400 hover:text-ink-700 text-xs">
            ✕
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {[
          { k: "now",  label: "In corso (±20')" },
          { k: "soon", label: "Prossimi 60'" },
          { k: "all",  label: "Tutto oggi" },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
                    data-testid={`svc-filter-${f.k}`}
                    className={`text-sm rounded-full px-3 py-1.5 border
                      ${filter === f.k ? "bg-bronze-600 text-white border-bronze-600"
                                        : "border-sand-300 hover:bg-sand-100"}`}>
            {f.label}
          </button>
        ))}
        <label className="ml-auto text-xs text-ink-600 inline-flex items-center gap-1">
          <input type="checkbox" checked={showTerminal}
                  onChange={(e) => setShowTerminal(e.target.checked)}
                  data-testid="svc-show-terminal" />
          Mostra completate/annullate
        </label>
      </div>

      {loading && (
        <div className="text-center py-10 text-ink-400">Caricamento…</div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-10 text-ink-400" data-testid="svc-empty">
          Nessuna prenotazione attiva per questo filtro.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map((r) => (
          <ReservationCard key={r.id} r={r} onChange={change}
                            onCopyPhone={copyPhone} onAssign={assign}
                            zones={zones} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-sand-300 bg-white p-4" data-testid={testid}>
      <div className="text-xs text-ink-500">{label}</div>
      <div className="mt-1 font-sans text-3xl tabular-nums font-semibold">{value}</div>
    </div>
  );
}

function ReservationCard({ r, onChange, onCopyPhone, onAssign, zones }) {
  const nextActions = NEXT_ACTIONS[r.status] || [];
  const [tableDraft, setTableDraft] = useState(r.table_number || "");
  React.useEffect(() => { setTableDraft(r.table_number || ""); }, [r.table_number]);

  const commitTable = () => {
    const v = (tableDraft || "").trim();
    if ((v || null) === (r.table_number || null)) return;   // no change
    onAssign(r, { table_number: v });
  };
  return (
    <div className={`rounded-2xl border-2 bg-white p-4 ${STATUS_STYLES[r.status] || ""}`}
          data-testid={`svc-card-${r.id}`}>
      {/* Header row: time, name, pax */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-2xl font-sans tabular-nums leading-none font-semibold">
            <Clock className="w-5 h-5 opacity-60" strokeWidth={1.5} />
            {r.time}
            <span className="text-ink-400 text-sm font-sans">· {r.pax}p</span>
            {r.table_number && (
              <span className="ml-2 rounded-md border border-current px-1.5 py-0.5 text-xs font-mono">
                {r.table_number}
              </span>
            )}
          </div>
          <div className="mt-2 font-semibold text-lg">
            {r.customer_name} {r.customer_surname || ""}
          </div>
          <div className="text-xs text-ink-500 flex items-center gap-2">
            <Phone className="w-3 h-3" /> {r.customer_phone || "—"}
            {r.customer_phone && (
              <button onClick={() => onCopyPhone(r)} title="Copia"
                        data-testid={`svc-copy-${r.id}`}
                        className="p-0.5 opacity-60 hover:opacity-100">
                <CopyIcon className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider opacity-80">
            {STATUS_LABELS[r.status]}
          </span>
          {r.zone && <span className="text-xs text-ink-600">{r.zone}</span>}
          <span className="text-[10px] text-ink-400 font-mono">{r.code}</span>
        </div>
      </div>

      {/* Alerts row */}
      {(r.allergies?.length > 0 || r.occasion || r.notes) && (
        <div className="mt-3 space-y-1">
          {r.allergies?.length > 0 && (
            <div className="flex items-start gap-1 text-xs text-rose-800">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              <span className="font-medium">Allergie:</span>{" "}
              <span>{r.allergies.join(", ")}</span>
            </div>
          )}
          {r.occasion && (
            <div className="flex items-start gap-1 text-xs text-amber-800">
              <PartyPopper className="w-3.5 h-3.5 mt-0.5" />
              <span>{r.occasion}</span>
            </div>
          )}
          {r.notes && (
            <div className="text-xs italic text-ink-600 line-clamp-2">
              "{r.notes}"
            </div>
          )}
        </div>
      )}

      {/* Assegnazione Sala + Tavolo */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <label className="text-xs text-ink-600">
          <span className="flex items-center gap-1 mb-1">
            <MapPin className="w-3 h-3" /> Sala
          </span>
          <select
            data-testid={`svc-zone-${r.id}`}
            value={r.zone || ""}
            onChange={(e) => onAssign(r, { zone: e.target.value || null })}
            className="w-full text-sm rounded-md border border-sand-300 bg-white px-2 py-1.5 cursor-pointer">
            <option value="">— seleziona sala —</option>
            {zones.map((z) => (<option key={z} value={z}>{z}</option>))}
          </select>
        </label>
        <label className="text-xs text-ink-600">
          <span className="flex items-center gap-1 mb-1">
            <Hash className="w-3 h-3" /> Tavolo
          </span>
          <input
            type="text"
            data-testid={`svc-table-${r.id}`}
            value={tableDraft}
            onChange={(e) => setTableDraft(e.target.value)}
            onBlur={commitTable}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
            placeholder="es. 12, T3, B1…"
            className="w-full text-sm rounded-md border border-sand-300 bg-white px-2 py-1.5 font-mono" />
        </label>
      </div>

      {/* Big status buttons */}
      {nextActions.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {nextActions.map(({ to, label, Icon, tone }) => (
            <button key={to} onClick={() => onChange(r, to)}
                      data-testid={`svc-btn-${r.id}-${to}`}
                      className={`min-h-[52px] rounded-xl border font-semibold text-sm
                                    inline-flex items-center justify-center gap-2 transition-transform
                                    active:scale-[0.98]
                        ${tone === "danger"
                          ? "border-rose-400 text-rose-800 bg-white hover:bg-rose-50"
                          : "bg-bronze-600 text-white border-bronze-600 hover:bg-bronze-700"}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} /> {label}
            </button>
          ))}
        </div>
      )}

      {/* Correzione stato (uso raro, per errori) */}
      <div className="mt-3 flex justify-end">
        <label className="text-xs inline-flex items-center gap-1 text-ink-500"
                title="Modifica stato — solo per correggere un errore">
          <Pencil className="w-3 h-3" />
          <select
            data-testid={`svc-override-${r.id}`}
            value=""
            onChange={(e) => {
              const to = e.target.value;
              if (!to || to === r.status) return;
              if (!window.confirm(`Cambiare stato in "${STATUS_LABELS[to]}"?\n\nUsa questa opzione solo per correggere un errore.`)) {
                e.target.value = "";
                return;
              }
              onChange(r, to, { force: true });
              e.target.value = "";
            }}
            className="text-xs rounded-md border border-sand-300 bg-white px-2 py-1 cursor-pointer">
            <option value="">Modifica stato…</option>
            {Object.entries(STATUS_LABELS)
              .filter(([k]) => k !== r.status)
              .map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
          </select>
        </label>
      </div>
    </div>
  );
}
